import {
  ALERT_CRITICALITY_LABELS,
  INITIATIVE_STATUS_LABELS,
  areaDashboardSchema,
  formatDate,
  formatDateTime,
  formatDecimal,
  targetPeriodLabel,
  type AreaDashboard,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronRight, FolderKanban } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { STATUS_DOT, StatusBadge } from '@/components/status/StatusBadge';
import { NativeSelect } from '@/components/ui/select';
import { actionLink, meetingLink } from '@/features/meetings/CriticalAnalysis';
import { drillHref, usePeriodParam } from '@/features/drill/drill';
import { DrillBadgeLink } from '@/features/drill/DrillLinks';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const pct = (v: number | null) => (v === null ? '—' : `${formatDecimal(v, 1)}%`);
const num = (v: number | null, unit: string) =>
  v === null
    ? '—'
    : unit === 'R$'
      ? `R$ ${formatDecimal(v, 2)}`
      : unit === '%'
        ? `${formatDecimal(v, 2)}%`
        : `${formatDecimal(v, 2)} ${unit}`;
const monthLabel = (year: number, month: number) => `${targetPeriodLabel('MONTH', month)}/${year}`;

/** Filtros no endereço: área, unidade e responsável (o link pode ser compartilhado). */
const PARAMS = { area: 'area', unit: 'unidade', responsible: 'responsavel' } as const;

/**
 * Dashboard por área (T-143b, RF-36, RN-24; DEC-057): o que está sob a responsabilidade de quem vê (o escopo de
 * área do perfil), com os filtros de diretoria, gerência, departamento, unidade e responsável — objetivos, KPIs com as
 * metas do mês, iniciativas, ações e pendências.
 */
export function DashboardAreaPage() {
  const { organizationId = '' } = useParams();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer>
      <PageHeader
        title="Dashboard por área"
        description="O que está sob a sua responsabilidade: objetivos, indicadores e metas, iniciativas, ações e pendências das suas áreas."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O dashboard mostra a estratégia de um projeto. Quando a empresa tiver um projeto, ele aparece aqui."
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-6">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <Dashboard organizationId={organizationId} project={project} />
        </div>
      )}
    </PageContainer>
  );
}

function Dashboard({
  organizationId,
  project,
}: {
  organizationId: string;
  project: { id: string; horizonStart: number; horizonEnd: number };
}) {
  const { client } = useAuth();
  const me = useCurrentUser();
  const ids = {
    directorate: useId(),
    management: useId(),
    department: useId(),
    unit: useId(),
    responsible: useId(),
    year: useId(),
    month: useId(),
  };
  const [params, setParams] = useSearchParams();
  const departmentId = params.get(PARAMS.area);
  const businessUnitId = params.get(PARAMS.unit);
  const responsibleId = params.get(PARAMS.responsible);
  // O período escolhido (ou do link) fica no endereço (drill-down — T-144c).
  const [period, setPeriod] = usePeriodParam([project.horizonStart, project.horizonEnd]);
  const query = useQuery({
    queryKey: ['area-dashboard', project.id, departmentId, businessUnitId, responsibleId, period],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${project.id}/area-dashboard`, {
        query: {
          ...(departmentId ? { departmentId } : {}),
          ...(businessUnitId ? { businessUnitId } : {}),
          ...(responsibleId ? { responsibleId } : {}),
          ...(period ? { year: String(period.year), month: String(period.month) } : {}),
        },
        schema: areaDashboardSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  // Filtro do endereço fora do escopo (link antigo, troca de projeto): os filtros saem e a tela volta ao padrão.
  const outOfScope =
    query.isError &&
    (departmentId || businessUnitId || responsibleId) &&
    query.error instanceof ApiError &&
    query.error.status === 404;
  useEffect(() => {
    if (!outOfScope) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Object.values(PARAMS)) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [outOfScope, setParams]);
  // A última resposta boa mantém os filtros na tela se uma troca falhar.
  const lastGood = useRef<AreaDashboard | null>(null);
  if (query.data) lastGood.current = query.data;

  if (query.isPending) return <LoadingState rows={6} label="Carregando o dashboard" />;
  const d = query.data ?? lastGood.current;
  const shown = period ?? (d ? { year: d.year, month: d.month } : null);
  const error = (
    <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
  );
  if (!d || !shown) return error;
  const updating = query.isFetching && query.isPlaceholderData;
  const years = Array.from(
    { length: project.horizonEnd - project.horizonStart + 1 },
    (_, i) => project.horizonStart + i,
  );

  // Cascata diretoria → gerência → departamento a partir da área escolhida.
  const byId = new Map(d.options.departments.map((x) => [x.id, x]));
  const chain: string[] = [];
  for (let id = departmentId; id && byId.has(id); id = byId.get(id)!.parentId) chain.unshift(id);
  const chosen = {
    DIRECTORATE: chain.find((id) => byId.get(id)?.type === 'DIRECTORATE') ?? '',
    MANAGEMENT: chain.find((id) => byId.get(id)?.type === 'MANAGEMENT') ?? '',
    DEPARTMENT: chain.find((id) => byId.get(id)?.type === 'DEPARTMENT') ?? '',
  };
  const under = (id: string, ancestor: string) => {
    for (let cur: string | null = id; cur; cur = byId.get(cur)?.parentId ?? null)
      if (cur === ancestor) return true;
    return false;
  };
  const optionsOf = (type: 'DIRECTORATE' | 'MANAGEMENT' | 'DEPARTMENT') =>
    d.options.departments.filter(
      (x) =>
        x.type === type &&
        (type === 'DIRECTORATE' ||
          !(type === 'DEPARTMENT' ? chosen.MANAGEMENT || chosen.DIRECTORATE : chosen.DIRECTORATE) ||
          under(x.id, type === 'DEPARTMENT' ? chosen.MANAGEMENT || chosen.DIRECTORATE : chosen.DIRECTORATE)),
    );
  // Escolher um nível limpa os de baixo; "todas" volta ao nível de cima.
  const pickArea = (level: 'DIRECTORATE' | 'MANAGEMENT' | 'DEPARTMENT', id: string) => {
    if (id) return setParam(PARAMS.area, id);
    const parent =
      level === 'DEPARTMENT'
        ? chosen.MANAGEMENT || chosen.DIRECTORATE
        : level === 'MANAGEMENT'
          ? chosen.DIRECTORATE
          : '';
    setParam(PARAMS.area, parent || null);
  };
  const areaSelect = (
    level: 'DIRECTORATE' | 'MANAGEMENT' | 'DEPARTMENT',
    label: string,
    all: string,
    id: string,
  ) => {
    const options = optionsOf(level);
    return (
      <div className="min-w-0">
        <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted-foreground">
          {label}
        </label>
        <NativeSelect
          id={id}
          value={chosen[level]}
          disabled={!options.length}
          onChange={(e) => pickArea(level, e.target.value)}
        >
          <option value="">{all}</option>
          {options.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </NativeSelect>
      </div>
    );
  };

  const filters = (
    <section aria-label="Filtros" className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {areaSelect('DIRECTORATE', 'Diretoria', 'Todas as diretorias', ids.directorate)}
        {areaSelect('MANAGEMENT', 'Gerência', 'Todas as gerências', ids.management)}
        {areaSelect('DEPARTMENT', 'Departamento', 'Todos os departamentos', ids.department)}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))_auto_auto]">
        <div className="min-w-0">
          <label htmlFor={ids.unit} className="mb-1 block text-xs font-medium text-muted-foreground">
            Unidade
          </label>
          <NativeSelect
            id={ids.unit}
            value={businessUnitId ?? ''}
            disabled={!d.options.businessUnits.length}
            onChange={(e) => setParam(PARAMS.unit, e.target.value || null)}
          >
            <option value="">Todas as unidades</option>
            {d.options.businessUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="min-w-0">
          <label htmlFor={ids.responsible} className="mb-1 block text-xs font-medium text-muted-foreground">
            Responsável
          </label>
          <NativeSelect
            id={ids.responsible}
            value={responsibleId ?? ''}
            onChange={(e) => setParam(PARAMS.responsible, e.target.value || null)}
          >
            <option value="">Todos os responsáveis</option>
            {d.options.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex gap-3">
          <div>
            <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
              Mês
            </label>
            <NativeSelect
              id={ids.month}
              value={shown.month}
              onChange={(e) => setPeriod({ year: shown.year, month: Number(e.target.value) })}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {targetPeriodLabel('MONTH', i + 1)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
              Ano
            </label>
            <NativeSelect
              id={ids.year}
              value={shown.year}
              onChange={(e) => setPeriod({ year: Number(e.target.value), month: shown.month })}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        {/* Só para quem é pessoa da empresa (Admin e Consultor Fluxus não são responsáveis aqui). */}
        {d.options.people.some((p) => p.id === me.id) ? (
          <label className="inline-flex min-h-11 items-center gap-2 self-end text-sm">
            <input
              type="checkbox"
              className="size-4 accent-(--brand)"
              checked={responsibleId === me.id}
              onChange={(e) => setParam(PARAMS.responsible, e.target.checked ? me.id : null)}
            />
            Só onde sou o responsável
          </label>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {updating
          ? 'Atualizando…'
          : query.isError
            ? null
            : d.computedAt
              ? `Dados de ${monthLabel(d.year, d.month).toLowerCase()}, calculados em ${formatDateTime(d.computedAt)}`
              : `Ainda não há performance calculada em ${monthLabel(d.year, d.month).toLowerCase()}`}
        {businessUnitId && !updating && !query.isError
          ? ' · Com unidade: KPIs com a meta e o realizado da unidade; objetivos não têm unidade.'
          : ''}
      </p>
    </section>
  );
  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        {filters}
        {error}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-6" aria-busy={query.isFetching}>
      {filters}
      <Summary d={d} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Objectives d={d} organizationId={organizationId} />
        <Pending d={d} organizationId={organizationId} />
      </div>
      <Kpis d={d} organizationId={organizationId} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Initiatives d={d} organizationId={organizationId} />
        <Actions d={d} organizationId={organizationId} />
      </div>
    </div>
  );
}

function Summary({ d }: { d: AreaDashboard }) {
  const s = d.summary;
  const lights = (x: NonNullable<AreaDashboard['summary']['objectives']>) =>
    `${x.red} vermelho${x.red === 1 ? '' : 's'} · ${x.yellow} amarelo${x.yellow === 1 ? '' : 's'} · ${x.green} verde${x.green === 1 ? '' : 's'}${x.gray ? ` · ${x.gray} sem dado${x.gray === 1 ? '' : 's'}` : ''}`;
  const cards: { label: string; value: number; detail: string; anchor: string }[] = [];
  if (s.objectives)
    cards.push({
      label: 'Objetivos',
      value: s.objectives.total,
      detail: lights(s.objectives),
      anchor: ANCHORS.objectives,
    });
  if (s.kpis)
    cards.push({ label: 'KPIs', value: s.kpis.total, detail: lights(s.kpis), anchor: ANCHORS.kpis });
  if (s.initiatives)
    cards.push({
      anchor: ANCHORS.initiatives,
      label: 'Iniciativas abertas',
      value: s.initiatives.open,
      detail: `${s.initiatives.late} atrasada${s.initiatives.late === 1 ? '' : 's'}`,
    });
  if (s.actions)
    cards.push({
      anchor: ANCHORS.actions,
      label: 'Ações abertas',
      value: s.actions.open,
      detail: `${s.actions.late} atrasada${s.actions.late === 1 ? '' : 's'}`,
    });
  cards.push({
    anchor: ANCHORS.pending,
    label: 'Pendências',
    value: s.pending,
    detail: 'ações atrasadas, resultados, alertas e decisões',
  });
  return (
    <section aria-label="Resumo da área">
      <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {cards.map((c) => (
          <li key={c.label}>
            <a
              href={`#${c.anchor}`}
              className="flex h-full flex-col gap-1 rounded-lg border border-border bg-surface p-4 hover:border-border-strong hover:bg-muted/40"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-2xl font-extrabold tabular-nums">{c.value}</span>
                <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              </span>
              <span className="text-sm font-medium">{c.label}</span>
              <span className="text-xs text-muted-foreground">{c.detail}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Âncoras das listas: os números do resumo levam até elas. */
const ANCHORS = {
  objectives: 'area-objetivos',
  kpis: 'area-kpis',
  initiatives: 'area-iniciativas',
  actions: 'area-acoes',
  pending: 'area-pendencias',
} as const;

function Section({
  title,
  count,
  anchor,
  children,
}: {
  title: string;
  count?: number;
  anchor?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section
      id={anchor}
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-surface p-4"
    >
      <h2 id={id} className="flex items-center gap-2 text-sm font-bold">
        {title}
        {count !== undefined ? (
          <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">{count}</span>
        ) : null}
      </h2>
      {children}
    </section>
  );
}

function ItemLink({ to, children }: { to: string | null; children: ReactNode }) {
  return to ? (
    <Link to={to} className="flex gap-2 rounded-md px-1 py-2 hover:bg-muted/50">
      {children}
      <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
    </Link>
  ) : (
    <div className="flex gap-2 px-1 py-2">{children}</div>
  );
}

const More = ({ total, shown }: { total: number; shown: number }) =>
  total > shown ? <p className="text-xs text-muted-foreground">e mais {total - shown}</p> : null;

function Objectives({ d, organizationId }: { d: AreaDashboard; organizationId: string }) {
  const b = d.objectives;
  if (!b) return null;
  return (
    <Section title="Objetivos" count={b.total} anchor={ANCHORS.objectives}>
      {b.items.length ? (
        <ul aria-label="Objetivos da área" className="flex flex-col divide-y divide-border">
          {b.items.map((o) => (
            <li key={o.id}>
              <ItemLink
                to={drillHref.objective(organizationId, {
                  projectId: d.project.id,
                  objectiveId: o.id,
                  year: d.year,
                  month: d.month,
                })}
              >
                <span
                  className={cn('mt-1.5 size-2 shrink-0 rounded-full', STATUS_DOT[o.light])}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{o.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {[o.perspective, o.department, o.owner].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="shrink-0 font-bold tabular-nums">{pct(o.performance)}</span>
              </ItemLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhum objetivo com esses filtros.</p>
      )}
      <More total={b.total} shown={b.items.length} />
    </Section>
  );
}

function Kpis({ d, organizationId }: { d: AreaDashboard; organizationId: string }) {
  const can = useCan();
  const b = d.kpis;
  if (!b) return null;
  const seesHistory = can('metas-resultados', 'view');
  return (
    <Section title="KPIs e metas do mês" count={b.total} anchor={ANCHORS.kpis}>
      {b.items.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">KPIs da área com a meta e o realizado do mês e o acumulado</caption>
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="py-1 text-left font-medium">
                  Indicador
                </th>
                <th scope="col" className="py-1 pl-3 text-right font-medium">
                  Meta
                </th>
                <th scope="col" className="py-1 pl-3 text-right font-medium">
                  Realizado
                </th>
                <th scope="col" className="py-1 pl-3 text-left font-medium">
                  Mês
                </th>
                <th scope="col" className="hidden py-1 pl-3 text-right font-medium sm:table-cell">
                  Acumulado
                </th>
              </tr>
            </thead>
            <tbody>
              {b.items.map((k) => (
                <tr key={`${k.id}-${k.unit?.id ?? ''}`} className="border-t border-border">
                  <th scope="row" className="py-2 pr-2 text-left font-medium break-words">
                    {seesHistory ? (
                      <Link
                        className="text-brand-strong underline-offset-2 hover:underline dark:text-brand"
                        to={drillHref.kpi(organizationId, {
                          projectId: d.project.id,
                          kpiId: k.id,
                          year: d.year,
                          month: d.month,
                        })}
                      >
                        {k.code} · {k.name}
                      </Link>
                    ) : (
                      `${k.code} · ${k.name}`
                    )}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {k.unit ? `Unidade ${k.unit.name} · ` : ''}
                      {k.department} · {k.owner}
                    </span>
                    {/* No celular a coluna do acumulado some: ele vem aqui. */}
                    <span className="block text-xs font-normal text-muted-foreground tabular-nums sm:hidden">
                      Acumulado: {num(k.ytdActual, k.unitLabel)} de {num(k.ytdTarget, k.unitLabel)}
                    </span>
                  </th>
                  <td className="py-2 pl-3 text-right tabular-nums">{num(k.target, k.unitLabel)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">{num(k.actual, k.unitLabel)}</td>
                  <td className="py-2 pl-3">
                    {seesHistory ? (
                      // O número do KPI leva às unidades (na unidade do item, quando ele é de uma unidade).
                      <DrillBadgeLink
                        to={drillHref.unit(organizationId, {
                          projectId: d.project.id,
                          kpiId: k.id,
                          businessUnitId: k.unit?.id ?? null,
                          year: d.year,
                          month: d.month,
                        })}
                        label={`${k.code}${k.unit ? ` na ${k.unit.name}` : ''} ${
                          k.performance === null ? 'sem dados' : pct(k.performance)
                        }: ver por unidade`}
                      >
                        <StatusBadge
                          status={k.light}
                          label={k.performance === null ? 'Sem dados' : pct(k.performance)}
                        />
                      </DrillBadgeLink>
                    ) : (
                      <StatusBadge
                        status={k.light}
                        label={k.performance === null ? 'Sem dados' : pct(k.performance)}
                      />
                    )}
                  </td>
                  <td className="hidden py-2 pl-3 text-right tabular-nums sm:table-cell">
                    {num(k.ytdActual, k.unitLabel)} de {num(k.ytdTarget, k.unitLabel)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhum KPI com esses filtros.</p>
      )}
      <More total={b.total} shown={b.items.length} />
    </Section>
  );
}

function Initiatives({ d, organizationId }: { d: AreaDashboard; organizationId: string }) {
  const b = d.initiatives;
  if (!b) return null;
  return (
    <Section title="Iniciativas abertas" count={b.total} anchor={ANCHORS.initiatives}>
      {b.items.length ? (
        <ul aria-label="Iniciativas da área" className="flex flex-col divide-y divide-border">
          {b.items.map((i) => (
            <li key={i.id}>
              <ItemLink
                to={`/empresas/${organizationId}/execucao/iniciativas?projeto=${d.project.id}&iniciativa=${i.id}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{i.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {i.objective} · {INITIATIVE_STATUS_LABELS[i.status]}
                    {i.leader ? ` · ${i.leader}` : ''}
                    {i.late && i.endDate ? ` · atrasada (fim previsto ${formatDate(i.endDate)})` : ''}
                  </span>
                </span>
              </ItemLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhuma iniciativa aberta com esses filtros.</p>
      )}
      <More total={b.total} shown={b.items.length} />
    </Section>
  );
}

function Actions({ d, organizationId }: { d: AreaDashboard; organizationId: string }) {
  const b = d.actions;
  if (!b) return null;
  return (
    <Section title="Ações abertas" count={b.total} anchor={ANCHORS.actions}>
      {b.items.length ? (
        <ul aria-label="Ações da área" className="flex flex-col divide-y divide-border">
          {b.items.map((a) => (
            <li key={a.id}>
              <ItemLink to={actionLink(organizationId, d.project.id, a.planId, a.id)}>
                <span
                  className={cn(
                    'mt-1.5 size-2 shrink-0 rounded-full',
                    a.late ? 'bg-status-red-solid' : 'bg-status-gray-solid',
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{a.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {a.planName}
                    {a.dueDate ? ` · prazo ${formatDate(a.dueDate)}` : ' · sem prazo'}
                    {a.late ? ' (atrasada)' : ''}
                    {a.responsibleName ? ` · ${a.responsibleName}` : ''}
                  </span>
                </span>
              </ItemLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhuma ação aberta com esses filtros.</p>
      )}
      <More total={b.total} shown={b.items.length} />
    </Section>
  );
}

function Pending({ d, organizationId }: { d: AreaDashboard; organizationId: string }) {
  const can = useCan();
  const p = d.pending;
  const filtered = Boolean(d.filters.departmentId || d.filters.businessUnitId || d.filters.responsibleId);
  const seesResults = can('metas-resultados', 'view');
  return (
    <Section title="Pendências" count={d.summary.pending} anchor={ANCHORS.pending}>
      {d.summary.pending === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma pendência com esses filtros.</p>
      ) : null}
      {p.lateActions ? (
        <p className="text-sm">
          <Link
            className="font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
            to={`/empresas/${organizationId}/execucao/planos-acao?projeto=${d.project.id}&atrasadas=1`}
          >
            {p.lateActions === 1 ? '1 ação atrasada' : `${p.lateActions} ações atrasadas`}
          </Link>
          {filtered ? (
            <span className="text-xs text-muted-foreground">
              {' '}
              · a lista abre com todas as atrasadas do projeto que você vê
            </span>
          ) : null}
        </p>
      ) : null}
      {p.missingResults?.items.length ? (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">Resultados não lançados</h3>
          <ul aria-label="Resultados não lançados" className="flex flex-col divide-y divide-border">
            {p.missingResults.items.map((m) => (
              <li key={`${m.kpiId}-${m.unit ?? ''}`}>
                <ItemLink
                  to={
                    seesResults
                      ? `/empresas/${organizationId}/indicadores/resultados?projeto=${d.project.id}&ano=${d.year}&mes=${m.months.at(-1)}`
                      : null
                  }
                >
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-medium">
                      {m.code} · {m.name}
                      {m.unit ? ` (${m.unit})` : ''}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {m.months.map((x) => targetPeriodLabel('MONTH', x)).join(', ')}/{d.year} · {m.owner}
                    </span>
                  </span>
                </ItemLink>
              </li>
            ))}
          </ul>
          <More total={p.missingResults.total} shown={p.missingResults.items.length} />
        </div>
      ) : null}
      {p.openAlerts?.items.length ? (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">Alertas abertos</h3>
          <ul aria-label="Alertas abertos da área" className="flex flex-col divide-y divide-border">
            {p.openAlerts.items.map((a) => (
              <li key={a.id}>
                <ItemLink
                  to={`/empresas/${organizationId}/gestao/alertas?projeto=${d.project.id}&alerta=${a.id}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-medium">{a.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {ALERT_CRITICALITY_LABELS[a.criticality]} · desde {formatDate(a.createdAt.slice(0, 10))}
                    </span>
                  </span>
                </ItemLink>
              </li>
            ))}
          </ul>
          <More total={p.openAlerts.total} shown={p.openAlerts.items.length} />
        </div>
      ) : null}
      {p.pendingDecisions?.items.length ? (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">Decisões pendentes</h3>
          <ul aria-label="Decisões pendentes da área" className="flex flex-col divide-y divide-border">
            {p.pendingDecisions.items.map((x) => (
              <li key={x.id}>
                <ItemLink to={meetingLink(organizationId, d.project.id, x.meeting.id)}>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-medium">{x.text}</span>
                    <span className="block text-xs text-muted-foreground">
                      {x.meeting.title}
                      {x.dueDate ? ` · prazo ${formatDate(x.dueDate)}` : ''}
                      {x.late ? ' (vencido)' : ''}
                    </span>
                  </span>
                </ItemLink>
              </li>
            ))}
          </ul>
          <More total={p.pendingDecisions.total} shown={p.pendingDecisions.items.length} />
        </div>
      ) : null}
    </Section>
  );
}
